import React, { useState, useEffect } from 'react';
import './FineTuningJobs.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? `http://${window.location.hostname}:3002` : '');

export default function FineTuningJobs() {
  const [jobs, setJobs] = useState([]);
  const [jobStats, setJobStats] = useState(null);
  const [datasetStats, setDatasetStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [newJob, setNewJob] = useState({
    displayName: '',
    baseModel: 'gemini-1.5-flash-002',
    trainingDatasetUri: '',
    validationDatasetUri: '',
    epochCount: 3,
    learningRateMultiplier: 1.0,
    adapterSize: 4,
    userId: 'jmkim',
    groupId: '0001'
  });

  // Load data on mount and auto-refresh
  useEffect(() => {
    loadJobs();
    loadJobStats();
    loadDatasetStats();

    if (autoRefresh) {
      const interval = setInterval(() => {
        loadJobs();
        loadJobStats();
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadJobs = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/jobs?limit=50`);
      const data = await response.json();

      if (data.success) {
        setJobs(data.data);
      }
    } catch (error) {
      console.error('Failed to load jobs:', error);
    }
  };

  const loadJobStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/job-stats`);
      const data = await response.json();

      if (data.success) {
        setJobStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load job stats:', error);
    }
  };

  const loadDatasetStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/dataset/stats`);
      const data = await response.json();

      if (data.success) {
        setDatasetStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load dataset stats:', error);
    }
  };

  const handleCreateJob = async () => {
    if (!newJob.displayName) {
      alert('작업 이름을 입력하세요.');
      return;
    }

    if (!newJob.trainingDatasetUri) {
      alert('학습 데이터셋 URI를 입력하세요.');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/finetuning/job/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newJob)
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ 파인튜닝 작업이 생성되었습니다!\n\nJob ID: ${data.jobId}\nVertex Job ID: ${data.vertexJobId}`);
        setShowCreateModal(false);
        setNewJob({
          displayName: '',
          baseModel: 'gemini-1.5-flash-002',
          trainingDatasetUri: '',
          validationDatasetUri: '',
          epochCount: 3,
          learningRateMultiplier: 1.0,
          adapterSize: 4,
          userId: 'jmkim',
          groupId: '0001'
        });
        loadJobs();
        loadJobStats();
      } else {
        alert(`❌ 실패: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to create job:', error);
      alert('파인튜닝 작업 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshJob = async (jobId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/job/${jobId}/status`);
      const data = await response.json();

      if (data.success) {
        alert(`✅ 상태 업데이트 완료\n\n상태: ${data.stateDescription}\n${data.tunedModel ? `모델: ${data.tunedModel.name}` : ''}`);
        loadJobs();
      } else {
        alert(`❌ 실패: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to refresh job:', error);
      alert('상태 업데이트에 실패했습니다.');
    }
  };

  const handleCancelJob = async (jobId) => {
    const confirmed = window.confirm('이 작업을 취소하시겠습니까?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/finetuning/job/${jobId}/cancel`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ 작업이 취소되었습니다.');
        loadJobs();
      } else {
        alert(`❌ 실패: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to cancel job:', error);
      alert('작업 취소에 실패했습니다.');
    }
  };

  const handleSyncAll = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/finetuning/jobs/sync`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        alert(`✅ 동기화 완료\n\n업데이트된 작업: ${data.syncedCount}개\n확인한 작업: ${data.totalChecked}개`);
        loadJobs();
        loadJobStats();
      } else {
        alert(`❌ 실패: ${data.error}`);
      }
    } catch (error) {
      console.error('Failed to sync jobs:', error);
      alert('동기화에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseDataset = () => {
    if (datasetStats?.exists) {
      setNewJob(prev => ({
        ...prev,
        trainingDatasetUri: datasetStats.location
      }));
      setShowCreateModal(true);
    }
  };

  const getStateColor = (state) => {
    const colors = {
      'JOB_STATE_PENDING': '#f39c12',
      'JOB_STATE_RUNNING': '#3498db',
      'JOB_STATE_SUCCEEDED': '#27ae60',
      'JOB_STATE_FAILED': '#e74c3c',
      'JOB_STATE_CANCELLING': '#95a5a6',
      'JOB_STATE_CANCELLED': '#7f8c8d'
    };
    return colors[state] || '#95a5a6';
  };

  const getStateDescription = (state) => {
    const descriptions = {
      'JOB_STATE_PENDING': '대기 중',
      'JOB_STATE_RUNNING': '실행 중',
      'JOB_STATE_SUCCEEDED': '완료',
      'JOB_STATE_FAILED': '실패',
      'JOB_STATE_CANCELLING': '취소 중',
      'JOB_STATE_CANCELLED': '취소됨'
    };
    return descriptions[state] || state;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="finetuning-jobs">
      <div className="ftj-header">
        <h1>Vertex AI 파인튜닝 작업</h1>
        <p>Gemini 모델 파인튜닝 작업을 생성하고 모니터링하세요</p>
      </div>

      {/* Statistics Dashboard */}
      <div className="ftj-stats-dashboard">
        <div className="ftj-stat-card">
          <div className="ftj-stat-label">총 작업</div>
          <div className="ftj-stat-value">{jobStats?.total || 0}개</div>
        </div>
        <div className="ftj-stat-card">
          <div className="ftj-stat-label">실행 중</div>
          <div className="ftj-stat-value running">{(jobStats?.pending || 0) + (jobStats?.running || 0)}개</div>
        </div>
        <div className="ftj-stat-card">
          <div className="ftj-stat-label">완료</div>
          <div className="ftj-stat-value succeeded">{jobStats?.succeeded || 0}개</div>
        </div>
        <div className="ftj-stat-card">
          <div className="ftj-stat-label">실패</div>
          <div className="ftj-stat-value failed">{jobStats?.failed || 0}개</div>
        </div>
        <div className="ftj-stat-card">
          <div className="ftj-stat-label">데이터셋</div>
          <div className="ftj-stat-value dataset">
            {datasetStats?.exists ? `${datasetStats.entryCount}개 항목` : '없음'}
          </div>
          {datasetStats?.exists && (
            <button onClick={handleUseDataset} className="ftj-use-dataset-btn">
              사용하기
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="ftj-actions">
        <div className="ftj-actions-left">
          <label className="ftj-auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>자동 새로고침 (30초)</span>
          </label>
        </div>
        <div className="ftj-actions-right">
          <button onClick={loadJobs} disabled={loading} className="btn-refresh">
            새로고침
          </button>
          <button onClick={handleSyncAll} disabled={loading} className="btn-sync">
            Vertex AI 동기화
          </button>
          <button onClick={() => setShowCreateModal(true)} className="btn-create">
            새 작업 생성
          </button>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="ftj-table-container">
        {loading && <div className="ftj-loading">로딩 중...</div>}

        <table className="ftj-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>작업 이름</th>
              <th>기본 모델</th>
              <th>상태</th>
              <th>생성일</th>
              <th>시작일</th>
              <th>완료일</th>
              <th>학습 데이터</th>
              <th>생성자</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 ? (
              <tr>
                <td colSpan="10" className="ftj-no-data">
                  파인튜닝 작업이 없습니다
                </td>
              </tr>
            ) : (
              jobs.map(job => (
                <tr key={job.job_id}>
                  <td>{job.job_id}</td>
                  <td>
                    <div className="ftj-job-name">{job.display_name}</div>
                    {job.tuned_model_name && (
                      <div className="ftj-model-name">{job.tuned_model_name}</div>
                    )}
                  </td>
                  <td>{job.base_model}</td>
                  <td>
                    <span
                      className="ftj-state-badge"
                      style={{ backgroundColor: getStateColor(job.state) }}
                    >
                      {getStateDescription(job.state)}
                    </span>
                  </td>
                  <td className="ftj-date">{formatDate(job.created_at)}</td>
                  <td className="ftj-date">{formatDate(job.started_at)}</td>
                  <td className="ftj-date">{formatDate(job.completed_at)}</td>
                  <td className="ftj-count">
                    {job.training_example_count || '-'}
                  </td>
                  <td>{job.created_by}</td>
                  <td>
                    <div className="ftj-actions-cell">
                      <button
                        onClick={() => handleRefreshJob(job.job_id)}
                        className="btn-action"
                        title="상태 업데이트"
                      >
                        새로고침
                      </button>
                      {['JOB_STATE_PENDING', 'JOB_STATE_RUNNING'].includes(job.state) && (
                        <button
                          onClick={() => handleCancelJob(job.job_id)}
                          className="btn-action btn-cancel"
                          title="작업 취소"
                        >
                          취소
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Job Modal */}
      {showCreateModal && (
        <div className="ftj-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="ftj-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ftj-modal-header">
              <h2>새 파인튜닝 작업 생성</h2>
              <button onClick={() => setShowCreateModal(false)} className="ftj-modal-close">×</button>
            </div>

            <div className="ftj-modal-body">
              <div className="ftj-form-group">
                <label>작업 이름 *</label>
                <input
                  type="text"
                  value={newJob.displayName}
                  onChange={(e) => setNewJob(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="예: vehicle-ocr-v1"
                />
              </div>

              <div className="ftj-form-group">
                <label>기본 모델</label>
                <select
                  value={newJob.baseModel}
                  onChange={(e) => setNewJob(prev => ({ ...prev, baseModel: e.target.value }))}
                >
                  <option value="gemini-1.5-flash-002">Gemini 1.5 Flash</option>
                  <option value="gemini-1.5-pro-002">Gemini 1.5 Pro</option>
                </select>
              </div>

              <div className="ftj-form-group">
                <label>학습 데이터셋 URI *</label>
                <input
                  type="text"
                  value={newJob.trainingDatasetUri}
                  onChange={(e) => setNewJob(prev => ({ ...prev, trainingDatasetUri: e.target.value }))}
                  placeholder="gs://bucket-name/dataset.jsonl"
                />
              </div>

              <div className="ftj-form-group">
                <label>검증 데이터셋 URI (선택)</label>
                <input
                  type="text"
                  value={newJob.validationDatasetUri}
                  onChange={(e) => setNewJob(prev => ({ ...prev, validationDatasetUri: e.target.value }))}
                  placeholder="gs://bucket-name/validation.jsonl"
                />
              </div>

              <div className="ftj-form-row">
                <div className="ftj-form-group">
                  <label>Epoch 수</label>
                  <input
                    type="number"
                    value={newJob.epochCount}
                    onChange={(e) => setNewJob(prev => ({ ...prev, epochCount: parseInt(e.target.value) }))}
                    min="1"
                    max="10"
                  />
                </div>

                <div className="ftj-form-group">
                  <label>학습률 배수</label>
                  <input
                    type="number"
                    value={newJob.learningRateMultiplier}
                    onChange={(e) => setNewJob(prev => ({ ...prev, learningRateMultiplier: parseFloat(e.target.value) }))}
                    step="0.1"
                    min="0.1"
                    max="10"
                  />
                </div>

                <div className="ftj-form-group">
                  <label>어댑터 크기</label>
                  <input
                    type="number"
                    value={newJob.adapterSize}
                    onChange={(e) => setNewJob(prev => ({ ...prev, adapterSize: parseInt(e.target.value) }))}
                    min="1"
                    max="16"
                  />
                </div>
              </div>

              <div className="ftj-form-row">
                <div className="ftj-form-group">
                  <label>사용자 ID</label>
                  <input
                    type="text"
                    value={newJob.userId}
                    onChange={(e) => setNewJob(prev => ({ ...prev, userId: e.target.value }))}
                  />
                </div>

                <div className="ftj-form-group">
                  <label>그룹 ID</label>
                  <input
                    type="text"
                    value={newJob.groupId}
                    onChange={(e) => setNewJob(prev => ({ ...prev, groupId: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="ftj-modal-footer">
              <button onClick={() => setShowCreateModal(false)} className="btn-cancel-modal">
                취소
              </button>
              <button onClick={handleCreateJob} disabled={loading} className="btn-create-modal">
                {loading ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

